import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProgressBar from '../components/Shared/ProgressBar';
import { format, parse, isAfter, setYear, getYear, parseISO, isValid } from 'date-fns';

const ClientDetail = ({ isAgency = false }) => {
  const { id, agencyId } = useParams();
  const navigate = useNavigate();
  const { clients, plans, agencies, tasks, getClientUsedHours, updateClient, tags, addTask } = useAppContext();
  
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  
  const client = clients.find(c => c.id === id);
  const [editData, setEditData] = useState({});
  const [newTask, setNewTask] = useState({ title: '', priority: 'Medium', loggedHours: 0 });

  useEffect(() => {
    if (client) setEditData({ ...client });
  }, [client, isEditModalOpen]);

  const formatRenewalDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    
    const slashDate = parse(dateStr, 'dd/MM/yyyy', new Date());
    if (isValid(slashDate)) return format(slashDate, 'MMM d, yyyy');

    const oldSlashDate = parse(dateStr, 'MM/dd/yyyy', new Date());
    if (isValid(oldSlashDate)) return format(oldSlashDate, 'MMM d, yyyy');

    const isoDate = parseISO(dateStr);
    if (isValid(isoDate) && dateStr.includes('-') && dateStr.split('-')[0].length === 4) {
      return format(isoDate, 'MMM d, yyyy');
    }
    
    try {
      const today = new Date();
      const currentYear = getYear(today);
      const parsedCycle = parse(dateStr, 'MM-dd', new Date());
      if (isValid(parsedCycle)) {
        let nextOccurrence = setYear(parsedCycle, currentYear);
        if (isAfter(today, nextOccurrence)) nextOccurrence = setYear(nextOccurrence, currentYear + 1);
        return format(nextOccurrence, 'MMM d, yyyy');
      }
    } catch (e) {}
    
    return dateStr;
  };

  if (!client) return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center p-8 text-slate-400 font-bold text-sm">
      Client not found
    </div>
  );

  const plan = plans.find(p => p.id === client.planId) || {};
  const agency = agencies.find(a => a.id === client.agencyId) || {};
  const usedHours = getClientUsedHours(client.id);
  const clientTasks = tasks.filter(t => t.clientId === client.id).sort((a,b) => {
      const dateA = new Date(a.date || new Date()).getTime();
      const dateB = new Date(b.date || new Date()).getTime();
      return dateB - dateA;
  });

  const handleUpdate = (e) => {
    e.preventDefault();
    updateClient(client.id, editData);
    setIsEditModalOpen(false);
  };

  const handleCreateTask = (e) => {
    e.preventDefault();
    addTask({ ...newTask, clientId: client.id, status: 'Open' });
    setIsNewTaskModalOpen(false);
    setNewTask({ title: '', priority: 'Medium', loggedHours: 0 });
  };

  const handleTagToggle = (tagId) => {
    const currentTags = editData.tags || [];
    const newTags = currentTags.includes(tagId) 
      ? currentTags.filter(t => t !== tagId)
      : [...currentTags, tagId];
    setEditData({ ...editData, tags: newTags });
  };

  const handleBack = () => {
    if (isAgency && agencyId) {
      navigate(`/portal/${agencyId}`);
    } else {
      navigate(-1);
    }
  };

  const content = (
    <div className="space-y-6 sm:space-y-8 pb-10">
      <header className="bg-slate-900 p-6 sm:p-8 rounded-3xl border border-slate-800 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-teal-500/5 blur-[100px] -mr-32 -mt-32"></div>
        
        <div className="flex justify-between items-center mb-6 relative z-10">
          <button onClick={handleBack} className="text-slate-500 hover:text-teal-400 font-bold text-xs sm:text-sm uppercase tracking-widest flex items-center gap-2 transition-colors">
            <SafeIcon icon={FiIcons.FiArrowLeft} /> {isAgency ? 'Portal' : 'Back'}
          </button>

          <button onClick={() => setIsEditModalOpen(true)} className="flex items-center gap-2 px-4 py-2 bg-slate-800/50 hover:bg-slate-800 text-slate-300 hover:text-teal-400 border border-slate-700 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-widest transition-colors shadow-sm">
            <SafeIcon icon={FiIcons.FiEdit3} /> Edit Client
          </button>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 relative z-10">
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-teal-600 flex items-center justify-center text-3xl sm:text-4xl font-black text-white shadow-lg shrink-0 overflow-hidden">
              {client.website ? (
                 <img 
                    src={`https://logo.clearbit.com/${client.website}`} 
                    alt={client.name}
                    className="w-full h-full object-cover"
                    onError={(e) => { e.target.parentElement.innerHTML = `<span>${(client.name || '?')[0]}</span>`; }}
                 />
              ) : (
                (client.name || '?')[0]
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <h1 className="text-2xl sm:text-4xl font-black text-white truncate">{client.name}</h1>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1 sm:gap-y-2 text-slate-500 font-bold text-xs sm:text-sm uppercase tracking-wide">
                <p><span style={{ color: plan.color || '#14b8a6' }}>{plan.name || 'N/A'}</span></p>
                <span className="hidden sm:inline text-slate-700">•</span>
                <p>Renewal: <span>{formatRenewalDate(client.resetDate)}</span></p>
                {client.website && (
                  <>
                    <span className="hidden sm:inline text-slate-700">•</span>
                    <a href={client.website.startsWith('http') ? client.website : `https://${client.website}`} target="_blank" rel="noopener noreferrer" className="text-teal-400 hover:underline truncate max-w-[150px] sm:max-w-none lowercase">
                        {client.website.replace(/^https?:\/\//, '')}
                    </a>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 sm:gap-2 justify-start md:justify-end w-full md:max-w-xs">
            {client.tags?.map(tid => {
              const tag = tags.find(t => t.id === tid);
              return tag && (
                <span key={tag.id} className="px-2.5 py-1 rounded-lg text-[10px] sm:text-xs font-black uppercase tracking-widest border" style={{ color: tag.color, backgroundColor: `${tag.color}15`, border: `1px solid ${tag.color}30` }}>
                  {tag.name}
                </span>
              );
            })}
          </div>
        </div>
        
        <div className="mt-8 sm:mt-10 max-w-2xl bg-slate-950/50 p-5 sm:p-6 rounded-2xl border border-slate-800 relative z-10">
           <div className="flex justify-between items-end mb-4 gap-2">
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs font-bold text-slate-500 uppercase tracking-widest mb-1">Cycle Utilization</p>
                <p className="text-2xl sm:text-3xl font-black text-white truncate">{usedHours.toFixed(1)} <span className="text-slate-600 text-lg sm:text-xl">/ {plan.monthlyHours || 0}h</span></p>
              </div>
              <span className={`text-xs sm:text-sm font-black shrink-0 ${usedHours > (plan.monthlyHours || 0) ? 'text-red-400' : 'text-teal-400'}`}>
                {plan.monthlyHours ? Math.round((usedHours / plan.monthlyHours) * 100) : 0}%
              </span>
           </div>
           <ProgressBar used={usedHours} total={plan.monthlyHours || 0} colorClass="bg-teal-500" />
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-slate-900 rounded-3xl border border-slate-800 overflow-hidden font-bold">
            <div className="p-5 sm:p-6 border-b border-slate-800 bg-slate-800/20 flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-bold text-white flex items-center gap-2">
                <SafeIcon icon={FiIcons.FiClock} className="text-teal-400" />
                Cycle Activity
              </h2>
              <button onClick={() => setIsNewTaskModalOpen(true)} className="flex items-center gap-2 px-3 py-1.5 bg-teal-600 hover:bg-teal-500 text-white rounded-lg font-bold text-xs uppercase tracking-widest transition-colors shadow-sm">
                <SafeIcon icon={FiIcons.FiPlus} /> New Task
              </button>
            </div>
            <div className="divide-y divide-slate-800">
               {clientTasks.length === 0 && <div className="p-12 text-center text-slate-600 italic text-sm">No task history.</div>}
               {clientTasks.map(task => (
                 <div key={task.id} className="p-5 sm:p-6 hover:bg-slate-800/30 transition-colors flex justify-between items-center cursor-pointer gap-4" onClick={() => {
                   if (isAgency && agencyId) {
                     navigate(`/portal/${agencyId}/task/${task.id}`);
                   } else {
                     navigate(`/tasks/${task.id}`);
                   }
                 }}>
                   <div className="min-w-0">
                     <p className="font-bold text-slate-200 mb-1 text-sm sm:text-base line-clamp-1">{task.title}</p>
                     <p className="text-[10px] sm:text-xs text-slate-500 flex items-center gap-2 font-black uppercase tracking-wider">
                        <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${task.status === 'Open' ? 'bg-teal-500' : 'bg-slate-700'}`}></span>
                        {task.status}
                     </p>
                   </div>
                   <div className="text-right shrink-0">
                     <p className="font-mono font-black text-teal-400 text-base sm:text-lg">{(task.loggedHours || 0).toFixed(1)}h</p>
                     <p className="text-[10px] sm:text-xs text-slate-600 uppercase font-black">Logged</p>
                   </div>
                 </div>
               ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
           {client.notes && (
             <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl font-bold">
               <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Client Notes</h3>
               <p className="text-slate-300 text-sm whitespace-pre-wrap">{client.notes}</p>
             </div>
           )}

           {client.domain && (
             <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl font-bold">
               <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Domain Information</h3>
               <p className="text-slate-300 text-sm whitespace-pre-wrap">{client.domain}</p>
             </div>
           )}

           <div className="bg-slate-900 p-6 rounded-3xl border border-slate-800 shadow-xl font-bold">
              <h3 className="text-xs font-black text-slate-500 uppercase tracking-widest mb-4">Partner</h3>
              {agency.name ? (
                <div className="flex items-center gap-3 p-4 bg-slate-950 rounded-xl border border-slate-800">
                  <div className="w-3 h-3 sm:w-4 sm:h-4 rounded-full shrink-0" style={{ backgroundColor: agency.color }}></div>
                  <span className="font-bold text-slate-200 text-sm truncate">{agency.name}</span>
                </div>
              ) : (
                <p className="text-slate-600 text-sm font-bold italic">Direct Account</p>
              )}
           </div>
        </div>
      </div>

      {isNewTaskModalOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setIsNewTaskModalOpen(false)}>
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-black text-white mb-8">New Support Task</h2>
            <form className="space-y-6" onSubmit={handleCreateTask}>
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Task Subject</label>
                <input autoFocus className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white outline-none focus:ring-2 focus:ring-teal-500 transition-all font-bold text-sm" value={newTask.title} onChange={e => setNewTask({ ...newTask, title: e.target.value })} placeholder="e.g. Homepage Update" required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Priority</label>
                  <select className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white outline-none focus:ring-2 focus:ring-teal-500 transition-all font-bold text-sm appearance-none" value={newTask.priority} onChange={e => setNewTask({ ...newTask, priority: e.target.value })}>
                    <option>Low</option>
                    <option>Medium</option>
                    <option>High</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-3">Initial Hours</label>
                  <input type="number" step="0.5" className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3.5 text-white outline-none focus:ring-2 focus:ring-teal-500 transition-all font-bold text-sm" value={newTask.loggedHours} onChange={e => setNewTask({ ...newTask, loggedHours: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
              <div className="flex flex-col gap-3 pt-6">
                <button type="submit" className="w-full px-4 py-3.5 bg-teal-600 text-white rounded-xl font-black hover:bg-teal-500 shadow-lg shadow-teal-900/40 text-sm uppercase">Create Task</button>
                <button type="button" onClick={() => setIsNewTaskModalOpen(false)} className="w-full px-6 py-2 text-slate-500 hover:text-white font-bold text-sm uppercase">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEditModalOpen && (
        <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-md flex items-center justify-center z-50 p-4" onClick={(e) => e.target === e.currentTarget && setIsEditModalOpen(false)}>
          <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 sm:p-8 w-full max-w-3xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-black text-white mb-8">Edit Profile</h2>
            <form onSubmit={handleUpdate} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Name</label>
                    <input className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none font-bold text-sm" value={editData.name || ''} onChange={e => setEditData({...editData, name: e.target.value})} required />
                </div>
                <div>
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Website</label>
                    <input className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none font-bold text-sm" placeholder="example.com" value={editData.website || ''} onChange={e => setEditData({...editData, website: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Client Notes</label>
                    <textarea className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none font-bold text-sm resize-none" rows="4" placeholder="Important details..." value={editData.notes || ''} onChange={e => setEditData({...editData, notes: e.target.value})}></textarea>
                </div>
                <div>
                    <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Domain Information</label>
                    <textarea className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none font-bold text-sm resize-none" rows="4" placeholder="Registrar, DNS settings..." value={editData.domain || ''} onChange={e => setEditData({...editData, domain: e.target.value})}></textarea>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Reset Cycle</label>
                  <input placeholder="DD/MM/YYYY" className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none font-bold text-sm" value={editData.resetDate || ''} onChange={e => setEditData({...editData, resetDate: e.target.value})} required />
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Tier</label>
                  <select className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none font-bold text-sm appearance-none" value={editData.planId || ''} onChange={e => setEditData({...editData, planId: e.target.value})} >
                    <option value="">Select Plan</option>
                    {plans.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Partner Agency</label>
                  <select className="w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none font-bold text-sm appearance-none" value={editData.agencyId || ''} onChange={e => setEditData({...editData, agencyId: e.target.value})} >
                    <option value="">Direct Account</option>
                    {agencies.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              </div>

              {tags.length > 0 && (
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Tags & Labels</label>
                  <div className="flex flex-wrap gap-2">
                    {tags.map(tag => {
                      const isSelected = (editData.tags || []).includes(tag.id);
                      return (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => handleTagToggle(tag.id)}
                          className={`px-3 py-1.5 rounded-xl text-xs font-black uppercase tracking-widest border transition-all duration-200 ${
                            isSelected 
                              ? 'shadow-lg shadow-black/20' 
                              : 'opacity-50 hover:opacity-80'
                          }`}
                          style={{ 
                            color: tag.color || '#94a3b8', 
                            backgroundColor: isSelected ? `${tag.color || '#94a3b8'}20` : 'transparent', 
                            borderColor: isSelected ? (tag.color || '#94a3b8') : `${tag.color || '#94a3b8'}40` 
                          }}
                        >
                          {tag.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-6">
                <button type="button" onClick={() => setIsEditModalOpen(false)} className="px-5 py-2 text-slate-500 hover:text-white font-bold transition-colors text-sm uppercase">Cancel</button>
                <button type="submit" className="px-8 py-3 bg-teal-600 text-white rounded-xl font-black hover:bg-teal-500 shadow-lg transition-all text-sm uppercase">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  return isAgency ? (
    <div className="min-h-screen bg-slate-950 p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {content}
      </div>
    </div>
  ) : content;
};

export default ClientDetail;